
import { useState } from 'react';
import {
  Box,
  ButtonBase,
  Grid,
  Link,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  DashboardThemeProvider,
  ErrorState,
  KpiCardsSkeleton,
  PageHeader,
  SectionErrorBoundary,
} from '../Common/DashboardKit';
import { formatByType, formatNumber } from '../../utils/dashboardFormatters';
import {
  useEffortsDashboardSummary,
  useEffortsBandCounts,
  useEffortsInsights,
} from './hooks/useEffortsDashboard.js';
import DesignationTrendTab from './components/DesignationTrendTab.jsx';
import EffortByGroupTab from './components/EffortByGroupTab.jsx';
import EffortsFilterBar from './components/EffortsFilterBar.jsx';
import BandDrilldownModal from './components/BandDrilldownModal.jsx';
import FlsCoverageTrendTab from './components/FlsCoverageTrendTab.jsx';
import ManagersTrendTab from './components/ManagersTrendTab.jsx';
import EffortVsPerformanceTab from './components/EffortVsPerformanceTab.jsx';
import './EffortsDashboard.css';

/** Icon name (from the backend `icon` field) → MUI icon component. */
const INSIGHT_ICONS = {
  info: InfoOutlinedIcon,
  warning: WarningAmberIcon,
  warn: WarningAmberIcon,
  error: ErrorOutlineIcon,
  danger: ErrorOutlineIcon,
  check: CheckCircleOutlineIcon,
  good: CheckCircleOutlineIcon,
  success: CheckCircleOutlineIcon,
  'trending-up': TrendingUpIcon,
  trendingUp: TrendingUpIcon,
  'trending-down': TrendingDownIcon,
  trendingDown: TrendingDownIcon,
  group: GroupsIcon,
  groups: GroupsIcon,
  'person-off': PersonOffIcon,
  trophy: EmojiEventsIcon,
  award: EmojiEventsIcon,
};

/** Resolve the icon component for an insight, falling back on tone. */
function insightIconFor(insight) {
  const byName = insight.icon ? INSIGHT_ICONS[insight.icon] : null;
  if (byName) return byName;
  return INSIGHT_ICONS[insight.tone] ?? InfoOutlinedIcon;
}

/** Replace each literal "None" token in insight text with an em dash. */
function nullSafeText(text) {
  if (!text) return text;
  return text.replace(/\bNone\b/g, '—');
}

// ── Reference values copied from the SalesPulse dashboard (effort module) ────
// Source: SalesPulse_-_Performance_Dashboard_by_SFE.html → injectEffortCss()
//   .eff-card / .eff-ic / .eff-val / .eff-band2* and the .modal* / .eff-ins* rules.
// Band tile tints (.eff-b-good/mid/warn/bad) — fill + text, no border.
const BAND_PALETTE = [
  { bg: '#fdECEC', text: '#d64545' }, // bad  (worst band)
  { bg: '#fdf4e3', text: '#b7791f' }, // warn
  { bg: '#eaf1fe', text: '#3567d6' }, // mid
  { bg: '#e9f7ef', text: '#1f8f4e' }, // good (best band)
];

/** Palette for a band at `index`, cycling if there are ever more than four. */
const bandColor = (index) => BAND_PALETTE[index % BAND_PALETTE.length];

// KPI card accent (top bar + icon tile) per card position: blue, teal, purple, orange.
const KPI_ACCENTS = [
  { acc: '#3B82F6', accbg: '#eaf1fe' },
  { acc: '#14B8A6', accbg: '#e4f7f3' },
  { acc: '#8B5CF6', accbg: '#f0ebfe' },
  { acc: '#F59E0B', accbg: '#fdf3e2' },
];
const kpiAccent = (index) => KPI_ACCENTS[index % KPI_ACCENTS.length];

/** Icon per KPI card id, matching the reference card order. */
const KPI_ICON = {
  workingDays: InsertInvitationIcon,
  drCallAverage: PhoneInTalkIcon,
  coverage: TrackChangesIcon,
  compliance: TaskAltIcon,
};

/**
 * KPI card TITLES, verbatim from the SalesPulse reference (`CM[].label`).
 * These override whatever the backend `kpi.label` carries so the cards always
 * read as the BA specified. Keyed by the card id.
 */
const KPI_TITLE = {
  workingDays: 'Avg. Working Days · FLS',
  drCallAverage: 'Dr. Call Avg · FLS',
  coverage: 'Doctor Coverage % · FLS',
  compliance: 'Visit Freq. Compliance % · FLS',
};

/**
 * The four small band-tile LABELS per card, verbatim from the reference
 * `EFF_LBL`. Applied positionally (band index 0..3) so the tiles always carry
 * the BA's thresholds even if the backend omits/renames band labels.
 *   working days: <20 / 20–22 / 22–24 / >24 Days
 *   dr call avg : <8 / 8–10 / 10–12 / >12  (reference EFF_LBL.dr)
 *   coverage    : <85% / 85–90% / 90–95% / >95%
 *   compliance  : <80% / 80–85% / 85–90% / >90%
 */
const KPI_BAND_LABELS = {
  workingDays: ['<20 Days', '20–22 Days', '22–24 Days', '>24 Days'],
  drCallAverage: ['<8', '8–10', '10–12', '>12'],
  coverage: ['<85%', '85–90%', '90–95%', '>95%'],
  compliance: ['<80%', '80–85%', '85–90%', '>90%'],
};

// Insight tone → left-bar colour + tint (.eff-ins.good/warn/bad/info).
const INSIGHT_TONE = {
  good: { bar: '#1f8f4e', bg: '#f0f9f3' },
  warn: { bar: '#d9a520', bg: '#fdf8ec' },
  danger: { bar: '#d64545', bg: '#fdf0f0' },
  bad: { bar: '#d64545', bg: '#fdf0f0' },
  info: { bar: '#3567d6', bg: '#eef3fe' },
};
const NAVY = '#0F1E3D';
const INSIGHT_LINK = '#3567d6';

export default function EffortsDashboard() {
  const [tab, setTab] = useState(0);
  // Drill-down modal state. `null` = closed. Either a band drill
  // ({ kind:'band', metricId, band, ... }) or a preset drill
  // ({ kind:'preset', preset, title }).
  const [drilldown, setDrilldown] = useState(null);

  // Open the drill-down modal for a given drill descriptor. The modal title is
  // the backend-provided short `modalTitle` (NOT the long insight sentence).
  // `drill` is {metric,band} or {preset}; `modalTitle` falls back to text.
  const openDrill = (drill, modalTitle) => {
    if (!drill) return;
    const title = modalTitle || '';
    if (drill.preset) {
      setDrilldown({ kind: 'preset', preset: drill.preset, title });
    } else if (drill.metric != null && drill.band != null) {
      // Forward the API metric EXACTLY as given (working_days, dr_call_avg,
      // coverage, compliance) — do not remap to the camelCase card id.
      setDrilldown({ kind: 'band', metricId: drill.metric, metricLabel: title, band: drill.band });
    }
  };

  // Cascade the three page-level calls top-to-bottom instead of firing them all
  // at mount: KPI values (summary) load first, the band-tile counts load once
  // summary has resolved, and the Auto Insights load once band counts are in.
  // This spreads backend load rather than hitting /summary, /band-counts and
  // /insights simultaneously. React Query caches each by key, so this ordering
  // only applies to the initial fetch (and to a filter-change refetch).
  const summaryQuery = useEffortsDashboardSummary();
  const bandCountsQuery = useEffortsBandCounts({ enabled: summaryQuery.isSuccess });
  const insightsQuery = useEffortsInsights({ enabled: bandCountsQuery.isSuccess });

  const kpis = Array.isArray(summaryQuery.data)
    ? summaryQuery.data
    : (summaryQuery.data?.kpis ?? []);

  const bandCards = bandCountsQuery.data?.cards ?? [];

  const insights = Array.isArray(insightsQuery.data)
    ? insightsQuery.data
    : (insightsQuery.data?.insights ?? []);

  // Show the page-level skeleton on first load AND while a filter change is
  // being processed (refetch in flight), so the cards reflect the applied
  // filters instead of showing stale values.
  const isLoading =
    summaryQuery.isLoading ||
    bandCountsQuery.isLoading ||
    insightsQuery.isLoading ||
    summaryQuery.isFetching ||
    bandCountsQuery.isFetching ||
    insightsQuery.isFetching;

  const isError =
    summaryQuery.isError ||
    bandCountsQuery.isError ||
    insightsQuery.isError;

  return (
    <DashboardThemeProvider>
    <Box className="efforts-page dk-root">
      <PageHeader subtitle="AI Efforts Tracker" />

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Efforts
      </Typography>

      <EffortsFilterBar />

      {isError ? (
        <ErrorState
          title="Failed to load efforts data."
          error={summaryQuery.error ?? bandCountsQuery.error ?? insightsQuery.error}
          onRetry={() => {
            summaryQuery.refetch();
            bandCountsQuery.refetch();
            insightsQuery.refetch();
          }}
          sx={{ mb: 2 }}
        />
      ) : null}

      {isLoading ? (
        <KpiCardsSkeleton count={4} />
      ) : (
        <>
          {/* ── KPI cards with band counts ── */}
          <SectionErrorBoundary label="efforts-kpis">
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {kpis.map((kpi, kpiIndex) => {
                const bands =
                  bandCards.find((card) => card.id === kpi.id)?.bands ?? [];
                const accent = kpiAccent(kpiIndex);
                const Icon = KPI_ICON[kpi.id] ?? InsertInvitationIcon;
                // Title + band-tile labels come from the reference (keyed by
                // card id), falling back to backend values if the id is new.
                const cardTitle = KPI_TITLE[kpi.id] ?? kpi.label;
                const tileLabels = KPI_BAND_LABELS[kpi.id] ?? null;

                return (
                  <Grid
                    key={kpi.id}
                    size={{ xs: 12, sm: 6, md: 3 }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        bgcolor: '#fff',
                        border: '1px solid #eef1f6',
                        borderRadius: '16px',
                        p: '15px 16px 14px',
                        height: '100%',
                        boxShadow: '0 1px 3px rgba(16,30,54,.06)',
                        transition: 'transform .15s ease, box-shadow .15s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 24px rgba(16,30,54,.10)',
                        },
                        // Top accent bar (.eff-card::before).
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          right: 0,
                          height: '4px',
                          bgcolor: accent.acc,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: accent.accbg,
                            color: accent.acc,
                            flex: '0 0 auto',
                          }}
                        >
                          <Icon sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '11.5px',
                            fontWeight: 800,
                            color: '#5b6472',
                            letterSpacing: '.4px',
                            textTransform: 'uppercase',
                            lineHeight: 1.2,
                          }}
                        >
                          {cardTitle}
                        </Typography>
                      </Box>

                      <Typography
                        sx={{ fontSize: '30px', fontWeight: 800, color: NAVY, lineHeight: 1, mb: '3px' }}
                      >
                        {formatByType(kpi.value, kpi.format)}
                      </Typography>

                      <Typography sx={{ fontSize: '11px', color: '#9aa3b2', mb: 1.5 }}>
                        no. of FLS · click a band
                      </Typography>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, 1fr)',
                          gap: 1,
                        }}
                      >
                        {bands.map((band, bandIndex) => {
                          const palette = bandColor(bandIndex);
                          const tileLabel =
                            tileLabels?.[bandIndex] ?? band.label;
                          return (
                            <ButtonBase
                              key={tileLabel ?? bandIndex}
                              onClick={() =>
                                setDrilldown({
                                  kind: 'band',
                                  metricId: kpi.id,
                                  metricLabel: cardTitle,
                                  band: bandIndex,
                                  bandLabel: tileLabel,
                                  count: band.count,
                                })
                              }
                              aria-label={`View ${cardTitle} ${tileLabel}: ${band.count} field users`}
                              sx={{
                                display: 'block',
                                bgcolor: palette.bg,
                                color: palette.text,
                                borderRadius: '11px',
                                textAlign: 'center',
                                py: '9px',
                                px: '6px',
                                width: '100%',
                                transition: 'transform .12s ease, box-shadow .12s ease',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 4px 12px rgba(16,30,54,.12)',
                                },
                                '&:focus-visible': {
                                  outline: '2px solid',
                                  outlineColor: palette.text,
                                  outlineOffset: 1,
                                },
                              }}
                            >
                              <Typography sx={{ fontSize: '17px', fontWeight: 800, lineHeight: 1 }}>
                                {formatNumber(band.count)}
                              </Typography>
                              <Typography
                                sx={{ fontSize: '9.5px', fontWeight: 700, mt: '3px', opacity: 0.8 }}
                              >
                                {tileLabel}
                              </Typography>
                            </ButtonBase>
                          );
                        })}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </SectionErrorBoundary>

          {/* ── Auto Insights ── */}
          <SectionErrorBoundary label="efforts-insights">
            {insights.length > 0 ? (
              <Box
                sx={{
                  mb: 3,
                  bgcolor: '#fff',
                  border: '1px solid #eef1f6',
                  borderRadius: '16px',
                  p: '14px 16px',
                  boxShadow: '0 1px 3px rgba(16,30,54,.06)',
                }}
              >
                <Typography
                  sx={{ fontSize: '15px', fontWeight: 800, color: NAVY, mb: 1.25 }}
                >
                  Auto Insights{' '}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, color: '#8a93a3', fontSize: '12px' }}
                  >
                    · click a card to view FLS
                  </Box>
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1.25,
                  }}
                >
                  {insights.map((insight, index) => {
                    const tone = INSIGHT_TONE[insight.tone] ?? INSIGHT_TONE.info;
                    const Icon = insightIconFor(insight);
                    // The HQ insight carries two drills (top / lowest HQ); a
                    // normal insight carries one. A card is clickable only when
                    // it has a single drill and a non-zero count.
                    const hasDual = Boolean(insight.drillTop && insight.drillLow);
                    const singleClickable =
                      !hasDual && Boolean(insight.drill) && insight.count !== 0;

                    const cardSx = {
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.25,
                      p: '10px 12px',
                      borderRadius: '10px',
                      borderLeft: '4px solid',
                      borderColor: tone.bar,
                      bgcolor: tone.bg,
                      transition: 'transform .16s ease, box-shadow .16s ease',
                      ...(singleClickable
                        ? {
                            cursor: 'pointer',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 14px rgba(16,30,54,.12)',
                            },
                          }
                        : {}),
                    };

                    return (
                      <Box
                        key={insight.id ?? index}
                        onClick={singleClickable ? () => openDrill(insight.drill, insight.modalTitle) : undefined}
                        sx={cardSx}
                      >
                        <Icon sx={{ color: tone.bar, fontSize: 18, mt: '2px', flex: '0 0 auto' }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{ fontSize: '13px', color: '#33405a', lineHeight: 1.45 }}
                          >
                            {nullSafeText(insight.text)}
                            {singleClickable ? (
                              <Box
                                component="span"
                                sx={{
                                  ml: 0.75,
                                  fontSize: '11px',
                                  fontWeight: 800,
                                  color: INSIGHT_LINK,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                → view FLS
                              </Box>
                            ) : null}
                          </Typography>

                          {/* HQ insight: two explicit links (top / lowest HQ). */}
                          {hasDual ? (
                            <Box sx={{ mt: 0.5, display: 'flex', gap: 2 }}>
                              <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => openDrill(insight.drillTop, insight.modalTitle)}
                                sx={{ fontSize: '11px', fontWeight: 800, color: INSIGHT_LINK }}
                              >
                                → top HQ
                              </Link>
                              <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => openDrill(insight.drillLow, insight.modalTitle)}
                                sx={{ fontSize: '11px', fontWeight: 800, color: INSIGHT_LINK }}
                              >
                                → lowest HQ
                              </Link>
                            </Box>
                          ) : null}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : null}
          </SectionErrorBoundary>

          {/* ── Dashboard tabs ── */}
          <SectionErrorBoundary label="efforts-tabs">
            <Box
              sx={{
                bgcolor: '#fff',
                border: '1px solid #eef1f6',
                borderRadius: '16px',
                p: 2,
                mb: 3,
                boxShadow: '0 1px 3px rgba(16,30,54,.06)',
              }}
            >
              <Tabs
                value={tab}
                onChange={(_event, value) => setTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  mb: 2,
                  borderBottom: '1px solid #eef1f6',
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '13px',
                    color: '#5b6472',
                    minHeight: 40,
                  },
                  '& .MuiTab-root.Mui-selected': { color: '#1657c8' },
                  '& .MuiTabs-indicator': { backgroundColor: '#2E7CF6', height: 3 },
                }}
              >
                <Tab label="Effort by Zone" />
                <Tab label="Trend by Designation" />
                <Tab label="FLS Coverage Trend" />
                <Tab label="Managers Trend" />
                <Tab label="Effort vs Performance" />
              </Tabs>

             

              {tab === 0 ? <EffortByGroupTab /> : null}

              {tab === 1 ? <DesignationTrendTab /> : null}

              {tab === 2 ? <FlsCoverageTrendTab /> : null}

              {tab === 3 ? <ManagersTrendTab /> : null}

              {tab === 4 ? <EffortVsPerformanceTab /> : null}
            </Box>
          </SectionErrorBoundary>
        </>
      )}

      <BandDrilldownModal
        open={Boolean(drilldown)}
        onClose={() => setDrilldown(null)}
        metricId={drilldown?.kind === 'band' ? drilldown.metricId : null}
        metricLabel={drilldown?.metricLabel}
        band={drilldown?.kind === 'band' ? (drilldown.band ?? null) : null}
        bandLabel={drilldown?.bandLabel}
        count={drilldown?.count}
        preset={drilldown?.kind === 'preset' ? drilldown.preset : null}
        title={drilldown?.title}
      />
    </Box>
    </DashboardThemeProvider>
  );
}